import React, { FC, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RouterProps } from 'react-router';
import { SubPage, Tab } from '@symfa-inc/providence-verizon-types';
import { Spin } from 'antd';
import { HttpService } from '@core/services';
import {
  EngineeringHttpService,
  EquipmentHttpService,
} from '@core/services/http';
import { getProjectBundleIDParamFromUrl } from '@core/utils/methods';
import { EditablePage } from '@shared/components';
import { EngineeringActions } from '@store/actions';
import { EngineeringSelectors, UserSelectors } from '@store/selectors';
import { EngineeringSOF } from './components';

type EngineeringSOFTabProps = RouterProps & {
  isActive?: boolean;
};

export const EngineeringSOFTab: FC<EngineeringSOFTabProps> = ({ isActive }) => {
  const dispatch = useDispatch();
  const [projectBundleID] = getProjectBundleIDParamFromUrl();
  const isLoading = useSelector(EngineeringSelectors.getTabLoadingState('sof'));
  const isDataLoaded = useSelector(
    EngineeringSelectors.getTabLoadedState('sof'),
  );
  const referencesLoading = useSelector(
    EngineeringSelectors.getTabLoadingState('sofReferences'),
  );
  const referencesLoaded = useSelector(
    EngineeringSelectors.getTabLoadedState('sofReferences'),
  );
  const equipmentTypeNames = useSelector(
    EngineeringSelectors.getEquipmentTypeNames,
  );
  const projectType = useSelector(EngineeringSelectors.getProjectType);
  const projectTypePermission = useSelector(
    UserSelectors.canEditProjectByProjectType(projectType),
  );
  const canEdit = useSelector(
    UserSelectors.canEdit('engineering', SubPage.EngineeringBrowse, Tab.SOF),
  );

  useEffect(() => {
    if (isActive && !isDataLoaded && !isLoading) {
      dispatch(
        EngineeringActions.setTabLoading.done({
          tabName: 'sof',
          isLoading: true,
        }),
      );

      const loadSOFTab = async (): Promise<void> => {
        try {
          await HttpService.getHttpRequests(
            EquipmentHttpService,
          ).getEquipmentTypes();
          await HttpService.getHttpRequests(
            EngineeringHttpService,
          ).getEngineeringSOF(projectBundleID);
        } catch {
          dispatch(
            EngineeringActions.setTabError.done({
              tabName: 'sof',
              hasError: true,
            }),
          );
        }
      };

      loadSOFTab();
    }
  }, [dispatch, isActive, isDataLoaded, isLoading, projectBundleID]);

  useEffect(() => {
    if (
      isActive &&
      isDataLoaded &&
      !referencesLoaded &&
      !referencesLoading &&
      equipmentTypeNames?.length
    ) {
      dispatch(
        EngineeringActions.setTabLoading.done({
          tabName: 'sofReferences',
          isLoading: true,
        }),
      );

      HttpService.getHttpRequests(EquipmentHttpService)
        .getEquipmentsByTypes(equipmentTypeNames)
        .then(() =>
          dispatch(
            EngineeringActions.setTabLoaded.done({
              tabName: 'sofReferences',
              isLoaded: true,
            }),
          ),
        )
        .catch(() =>
          dispatch(
            EngineeringActions.setTabError.done({
              tabName: 'sofReferences',
              hasError: true,
            }),
          ),
        );
    }
  }, [
    dispatch,
    equipmentTypeNames,
    isActive,
    isDataLoaded,
    referencesLoaded,
    referencesLoading,
  ]);

  if (
    isLoading ||
    !isDataLoaded ||
    referencesLoading ||
    (!!equipmentTypeNames?.length && !referencesLoaded)
  ) {
    return <Spin size="large" />;
  }

  return (
    <EditablePage
      editableComponent={EngineeringSOF}
      canUserEdit={canEdit && projectTypePermission}
    />
  );
};
